import { ReactNode } from 'react';
import { useGetList, Title } from 'react-admin';
import { Card, CardContent, Typography, Box, Button, Chip, Select, MenuItem, Alert } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import localforage from 'localforage';
import type { DebtSlip, PaymentOrder } from '../types';
import { formatVnd } from '../utils/format';
import { PageHeader } from '../components/PageHeader';
import { brand } from '../theme';
import { pageShellSx } from '../ui';

const Stat = ({
  label, value, sub, color, icon, trend,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  icon: ReactNode;
  trend?: string;
}) => (
  <Card sx={{ minHeight: 140, position: 'relative', overflow: 'hidden' }}>
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }} noWrap>{label}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 850, lineHeight: 1.18, mt: 0.45, fontSize: { xs: 27, md: 30 } }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.55 }} noWrap>{sub}</Typography>}
        </Box>
        <Box
          sx={{
            width: 46,
            height: 46,
            flexShrink: 0,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(color, 0.105),
            color,
            '& svg': { fontSize: 23 },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 1.1 }}>
        <TrendingUpRoundedIcon sx={{ fontSize: 15, color: trend ? 'success.main' : 'text.disabled' }} />
        <Typography variant="caption" sx={{ color: trend ? 'success.main' : 'text.disabled', fontWeight: 750 }}>
          {trend || '0%'}
        </Typography>
        <Typography variant="caption" color="text.secondary">so với kỳ trước</Typography>
      </Box>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) => (
  <Card sx={{ minWidth: 0, overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: { xs: 1.75, md: 2.4 }, pt: 2.2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(14,116,144,.09)', color: 'primary.main', '& svg': { fontSize: 19 } }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{title}</Typography>
      </Box>
      <Select value="7" size="small" sx={{ minWidth: 112, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
        <MenuItem value="7">7 ngày qua</MenuItem>
      </Select>
    </Box>
    <CardContent sx={{ px: { xs: 1, md: 1.8 }, pt: 0.8, pb: '12px !important' }}>{children}</CardContent>
  </Card>
);

export const Dashboard = () => {
  const { data: slips = [] } = useGetList<DebtSlip>('debtSlips', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' },
  });
  const { data: orders = [] } = useGetList<PaymentOrder>('paymentOrders', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' },
  });
  const { total: invoices } = useGetList('vatInvoices', { pagination: { page: 1, perPage: 1 } });
  const { total: suppliers } = useGetList('suppliers', { pagination: { page: 1, perPage: 1 } });

  const byStatus = (s: string) => slips.filter((x) => x.status === s).length;
  const ordersByStatus = (s: string) => orders.filter((o) => o.status === s);
  const pending = ordersByStatus('pending');
  const approved = ordersByStatus('approved');
  const rejected = ordersByStatus('rejected');
  const sum = (arr: PaymentOrder[]) => arr.reduce((a, o) => a + (o.amount || 0), 0);
  const totalOutstanding = sum(pending) + sum(approved);

  const resetDemo = async () => {
    if (!window.confirm('Xóa toàn bộ dữ liệu và nạp lại dữ liệu mẫu?')) return;
    const keys = await localforage.keys();
    await Promise.all(keys.filter((k) => k.startsWith('ai-cong-no-')).map((k) => localforage.removeItem(k)));
    window.location.reload();
  };

  return (
    <Box sx={pageShellSx}>
      <Title title="Tổng quan" />
      <PageHeader
        title="Tổng quan công nợ"
        subtitle="Cập nhật nhanh tình hình công nợ, đối soát và dòng tiền phải chi của doanh nghiệp."
        icon={<DashboardRoundedIcon />}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <ScheduleRoundedIcon sx={{ fontSize: 18 }} />
            <Typography variant="caption" sx={{ fontWeight: 650 }}>
              Cập nhật: {new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
        }
      />

      <Card
        sx={{
          mb: { xs: 2, md: 2.5 },
          color: '#fff',
          border: 'none',
          background: brand.heroGradient,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 22px 48px -34px rgba(9,45,61,.80)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle at 82% 20%, rgba(255,255,255,.10), transparent 28%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 260,
            height: 260,
            right: -60,
            top: -120,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,.14)',
          },
        }}
      >
        <CardContent
          sx={{
            position: 'relative',
            zIndex: 1,
            minHeight: { xs: 220, md: 180 },
            p: { xs: 2.5, sm: 3.2, md: 3.5 },
            '&:last-child': { pb: { xs: 2.5, sm: 3.2, md: 3.5 } },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2.5,
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.7 }}>
              <AutoAwesomeRoundedIcon sx={{ fontSize: 17, opacity: 0.85 }} />
              <Typography variant="overline" sx={{ opacity: 0.88 }}>Tổng công nợ cần chi</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 850, lineHeight: 1.05, fontSize: { xs: 38, sm: 48, lg: 52 } }}>
              {formatVnd(totalOutstanding)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.8 }}>
              Gồm lệnh chờ duyệt và đã duyệt nhưng chưa ghi nhận đã chi.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, minmax(180px, 1fr))' }, gap: 1.2 }}>
            <Box sx={{ p: 1.7, borderRadius: '12px', bgcolor: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.18)', backdropFilter: 'blur(8px)' }}>
              <Typography variant="caption" sx={{ opacity: 0.78 }}>Chờ duyệt</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.2 }}>{formatVnd(sum(pending))}</Typography>
            </Box>
            <Box sx={{ p: 1.7, borderRadius: '12px', bgcolor: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.18)', backdropFilter: 'blur(8px)' }}>
              <Typography variant="caption" sx={{ opacity: 0.78 }}>Đã duyệt</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.2 }}>{formatVnd(sum(approved))}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, gap: { xs: 1.25, md: 1.6 }, mb: { xs: 2, md: 2.5 } }}>
        <Stat label="Đơn nợ" value={slips.length} sub={`${byStatus('new')} mới`} color="#0E7490" icon={<ReceiptLongRoundedIcon />} />
        <Stat label="Chờ đối soát" value={byStatus('extracted')} color="#2F80ED" icon={<CompareArrowsRoundedIcon />} trend={byStatus('extracted') ? '↓ 50%' : undefined} />
        <Stat label="Đã đối soát" value={byStatus('matched')} color="#0E7490" icon={<FactCheckRoundedIcon />} />
        <Stat label="Lệnh chờ duyệt" value={pending.length} sub={formatVnd(sum(pending))} color="#F59E0B" icon={<HourglassTopRoundedIcon />} />
        <Stat label="Lệnh đã duyệt" value={approved.length} sub={formatVnd(sum(approved))} color="#0FAF78" icon={<CheckCircleRoundedIcon />} trend={approved.length ? '↑ 100%' : undefined} />
        <Stat label="Danh mục" value={`${suppliers || 0} NCC`} sub={`${invoices || 0} HĐ VAT`} color="#1D4E6D" icon={<Inventory2RoundedIcon />} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.08fr .92fr' }, gap: { xs: 1.5, md: 2 }, mb: 2 }}>
        <ChartCard title="Đơn nợ theo trạng thái" icon={<TrendingUpRoundedIcon />}>
          <BarChart
            height={315}
            xAxis={[{ scaleType: 'band', data: ['Mới', 'Đã bóc tách', 'Đã đối soát', 'Đã tạo lệnh'] }]}
            yAxis={[{ min: 0 }]}
            series={[{ data: [byStatus('new'), byStatus('extracted'), byStatus('matched'), byStatus('ordered')], label: 'Số đơn', color: '#0E7490' }]}
            borderRadius={12}
            grid={{ horizontal: true }}
            margin={{ left: 36, right: 16, top: 25, bottom: 40 }}
          />
        </ChartCard>
        <ChartCard title="Lệnh chi theo trạng thái" icon={<FactCheckRoundedIcon />}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.15fr .85fr' }, alignItems: 'center', minHeight: 315 }}>
            <PieChart
              height={285}
              series={[{
                innerRadius: 72,
                outerRadius: 112,
                paddingAngle: 2,
                cornerRadius: 6,
                data: [
                  { id: 0, value: pending.length, label: 'Chờ duyệt', color: '#F59E0B' },
                  { id: 1, value: approved.length, label: 'Đã duyệt', color: '#0FAF78' },
                  { id: 2, value: rejected.length, label: 'Từ chối', color: '#E94B5F' },
                ],
              }]}
              hideLegend
              margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
            />
            <Box sx={{ px: { xs: 2, sm: 0 }, pb: { xs: 2, sm: 0 } }}>
              {[
                ['Chờ duyệt', pending.length, '#F59E0B'],
                ['Đã duyệt', approved.length, '#0FAF78'],
                ['Từ chối', rejected.length, '#E94B5F'],
              ].map(([label, value, color]) => (
                <Box key={String(label)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.7 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>{value}</Typography>
                </Box>
              ))}
              <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ mt: 1.4, py: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 750 }}>
                  {approved.length ? 'Các lệnh hiện tại đã được phê duyệt.' : 'Chưa có lệnh được phê duyệt.'}
                </Typography>
              </Alert>
            </Box>
          </Box>
        </ChartCard>
      </Box>

      <Alert
        severity="error"
        variant="outlined"
        action={<Button color="error" variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetDemo}>Đặt lại dữ liệu mẫu</Button>}
        sx={{ bgcolor: 'rgba(233,75,95,.025)', '& .MuiAlert-message': { width: '100%' } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Đặt lại dữ liệu mẫu</Typography>
        <Typography variant="caption" color="text.secondary">Xóa toàn bộ dữ liệu hiện tại và nạp lại dữ liệu mẫu ban đầu.</Typography>
      </Alert>
    </Box>
  );
};
